import { useState, useEffect, useRef, useCallback } from 'react';
import { audioManager } from '../core/audio/AudioManagerAdapter';
import {
  AudioState,
  UseSacredAudioReturn,
  DEFAULT_ENVELOPE,
  DEFAULT_DURATIONS,
  VOLUME_RANGE
} from '../types/audio.types';

/**
 * Professional Sacred Audio Hook
 * 
 * Provides comprehensive audio generation for sacred frequencies, scales, and chords.
 * Uses the Web Audio API for precise frequency generation and professional sound design.
 * 
 * - Multiple sacred frequency systems (Fr00t, Solfeggio, Chakra)
 * - Complex chord and scale variations
 * - Golden ratio and harmonic series generation
 * - Experimental waveform options
 * - Power-user frequency generator
 * 
 * - Safe default volumes and frequencies
 * - Professional ADSR envelopes prevent audio clicks
 * - Clear error messages and validation
 * - Graceful audio context cleanup
 * - Full user control over all audio parameters
 * 
 * 
 * @returns {UseSacredAudioReturn} Audio state and control functions
 */

// FREQUENCY SETS - Experimental, no health claims
// TODO: Research psychoacoustics (Week 3) to determine what can be claimed
// Current status: These are traditional associations, NOT scientifically validated
const FREQUENCY_SETS = {
  // Musical frequencies in Hz (A = 432 Hz tuning)
  fr00t: {
    F: 432,      // A4 in 432 Hz tuning
    R: 528,      // C5 (approx)
    0: 396,      // G4 (approx)
    T: 639,      // Eb5 (approx)
  },
  // Traditional associations (requires cultural research for proper context)
  chakras: {
    root: 396,
    sacral: 417,
    solar: 528,
    heart: 639,
    throat: 741,
    third_eye: 852,
    crown: 963
  },
  // Historical Solfeggio scale (6-note medieval system, later expanded)
  // Modern associations require verification
  solfeggio: {
    ut: 396,
    re: 417,
    mi: 528,
    fa: 639,
    sol: 741,
    la: 852
  }
};

// ============================================================================
// SACRED AUDIO HOOK - Professional Implementation
// ============================================================================

/**
 * Main hook for sacred audio generation
 * Implements the Web Audio API with professional audio engineering principles
 */
export const useSacredAudio = (): UseSacredAudioReturn => {
  const [state, setState] = useState<AudioState>({
    isInitialized: false,
    isPlaying: false,
    currentFrequency: 528, // Default frequency (C5)
    volume: VOLUME_RANGE.default
  });

  // Use AudioManager for context and gain - no duplication
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const activeOscillatorsRef = useRef<OscillatorNode[]>([]);

  // ============================================================================
  // AUDIO CONTEXT INITIALIZATION
  // ============================================================================

  /**
   * Initialize the Web Audio API context via AudioManager
   * 
   * Audio Signal Flow:
   * Oscillator -> Main Gain -> Reverb -> Destination (managed by AudioManager)
   * 
   * This hook now uses AudioManager's main context instead of creating its own,
   * preventing AudioContext limit violations and enabling centralized management.
   * 
   * Will wait for AudioManager to initialize if not ready yet.
   */
  const initializeAudio = useCallback(async (): Promise<void> => {
    try {
      // If AudioManager isn't ready, initialize it first (requires user interaction)
      if (!audioManager.isReady()) {
        console.log('🎵 Sacred Audio: AudioManager not ready, waiting for initialization...');
        // We'll wait for it to be initialized by user interaction
        // Don't throw error - just mark as not initialized yet
        setState(prev => ({
          ...prev,
          isInitialized: false
        }));
        return;
      }

      // Get main audio context from AudioManager
      const mainContext = audioManager.getMainAudioContext();
      const mainGain = audioManager.getMainMasterGain();
      const reverb = audioManager.getReverbNode(); // Optional - reverb may not be initialized

      if (!mainContext || !mainGain) {
        console.warn('⚠️ Main audio context not fully available in AudioManager');
        setState(prev => ({
          ...prev,
          isInitialized: false
        }));
        return;
      }

      // Reverb is optional - log if not available but don't block initialization
      if (!reverb) {
        console.log('🎵 Reverb not available (non-critical)');
      }

      // Update state to reflect successful initialization
      setState(prev => ({
        ...prev,
        isInitialized: true
      }));

      console.log('🎵 Sacred Audio System Initialized');
      console.log('   Using AudioManager main context');
      console.log(`   Sample Rate: ${mainContext.sampleRate}Hz`);
      console.log(`   Volume: ${VOLUME_RANGE.default}dB`);
    } catch (error) {
      console.error('❌ Failed to initialize sacred audio system:', error);
      setState(prev => ({
        ...prev,
        isInitialized: false
      }));
    }
  }, []);

  // Initialize Sacred Audio (alias)
  const initializeSacredAudio = useCallback(async (): Promise<void> => {
    return initializeAudio();
  }, [initializeAudio]);

  // ============================================================================
  // SACRED TONE PLAYBACK - Professional ADSR Implementation
  // ============================================================================

  /**
   * Play a single sacred tone with professional envelope shaping
   * 
   * ADSR Envelope Explained:
   * - Attack: Time to reach peak amplitude (creates initial impact)
   * - Decay: Time to fall to sustain level (adds natural character)
   * - Sustain: Steady amplitude level during note hold (body of sound)
   * - Release: Time to fade to silence after note ends (natural tail-off)
   * 
   * This creates more natural, musical sounds compared to abrupt on/off
   * 
   * @param {number} frequency - Frequency in Hz (1-20000)
   * @param {number} duration - Total duration in seconds (default: 2.0)
   * @param {WaveformType} waveform - Oscillator waveform (default: 'sine')
   */
  const playSacredTone = useCallback((
    frequency: number, 
    duration: number = DEFAULT_DURATIONS.tone,
    waveform: 'sine' | 'square' | 'sawtooth' | 'triangle' = 'sine'
  ): void => {
    // Get context and gain from AudioManager
    const mainContext = audioManager.getMainAudioContext();
    const mainGain = audioManager.getMainMasterGain();

    if (!mainContext || !mainGain) {
      console.error('❌ Audio not initialized. Call initializeAudio() first.');
      return;
    }

    // Validate frequency range
    if (frequency < 1 || frequency > 20000) {
      console.error(`❌ Invalid frequency: ${frequency}Hz. Must be 1-20000 Hz.`);
      return;
    }

    try {
      // Stop any existing single-tone oscillator
      if (oscillatorRef.current) {
        try {
          oscillatorRef.current.stop();
          oscillatorRef.current.disconnect();
        } catch (e) {
          // Already stopped
        }
      }

      const currentTime = mainContext.currentTime;

      // Create oscillator node for tone generation
      // Use triangle wave for calmer, softer meditation tones
      const oscillator = mainContext.createOscillator();
      oscillator.type = 'triangle'; // Softer than sine for meditation
      oscillator.frequency.value = frequency;

      // Add low-pass filter to warm and soften the tone
      const lowpass = mainContext.createBiquadFilter();
      lowpass.type = 'lowpass';
      // Use frequency-dependent cutoff: lower frequencies get less filtering
      const cutoffFreq = Math.min(600, frequency * 3); // Cap at 600Hz, scale with frequency
      lowpass.frequency.value = cutoffFreq; // Warm, pleasant sound
      lowpass.Q.value = 0.5; // Very gentle filter curve for smoothness

      // Create dedicated gain node for ADSR envelope
      // This allows independent volume control separate from master volume
      const toneGainNode = mainContext.createGain();

      /**
       * Apply Gentle ADSR Envelope for Meditation
       * 
       * Calmer envelope values for meditation tones:
       * - Attack: 800ms (slow, gentle rise like ocean wave)
       * - Decay: 200ms (natural fall)
       * - Sustain: 0.5 (50% of peak - softer, less intrusive)
       * - Release: 1500ms (very slow, gentle fade like wave receding)
       */
      const gentleAttack = 0.8; // 800ms slow rise
      const gentleDecay = 0.2; // 200ms
      const gentleSustain = 0.5; // 50% sustain (softer)
      const gentleRelease = 1.5; // 1500ms slow fade
      
      const peakGain = 0.5; // Reduced peak for calmness (was 0.8)
      const sustainGain = peakGain * gentleSustain;

      // Set up gentle envelope automation
      toneGainNode.gain.setValueAtTime(0, currentTime);
      
      // Attack phase: 0 -> peak (slow, gentle rise)
      toneGainNode.gain.linearRampToValueAtTime(
        peakGain, 
        currentTime + gentleAttack
      );
      
      // Decay phase: peak -> sustain
      toneGainNode.gain.linearRampToValueAtTime(
        sustainGain, 
        currentTime + gentleAttack + gentleDecay
      );
      
      // Sustain phase: hold at sustain level
      toneGainNode.gain.setValueAtTime(
        sustainGain, 
        currentTime + duration - gentleRelease
      );
      
      // Release phase: sustain -> 0 (very slow, gentle fade)
      toneGainNode.gain.linearRampToValueAtTime(
        0, 
        currentTime + duration
      );

      // Add gentle frequency drift for natural, organic feel
      const driftLFO = mainContext.createOscillator();
      const driftGain = mainContext.createGain();
      driftLFO.type = 'sine';
      driftLFO.frequency.value = 0.1; // Very slow drift (10 second period)
      driftGain.gain.value = frequency * 0.01; // 1% gentle frequency variation
      
      driftLFO.connect(driftGain);
      driftGain.connect(oscillator.frequency);
      driftLFO.start(currentTime);
      driftLFO.stop(currentTime + duration);

      // Connect audio graph: Oscillator -> Lowpass -> Tone Gain (ADSR) -> Master Gain
      oscillator.connect(lowpass);
      lowpass.connect(toneGainNode);
      toneGainNode.connect(mainGain);

      // Schedule playback
      oscillator.start(currentTime);
      oscillator.stop(currentTime + duration);

      oscillatorRef.current = oscillator;

      // Update state
      setState(prev => ({
        ...prev,
        isPlaying: true,
        currentFrequency: frequency
      }));

      // Reset playing state after duration
      setTimeout(() => {
        setState(prev => ({
          ...prev,
          isPlaying: false
        }));
      }, duration * 1000);

      console.log(`🎶 Sacred Tone: ${frequency.toFixed(2)}Hz | ${waveform} wave | ${duration}s`);
      
    } catch (error) {
      console.error('❌ Failed to play sacred tone:', error);
    }
  }, []);

  // ============================================================================
  // SACRED CHORD PLAYBACK - Polyphonic Harmonic System
  // ============================================================================

  /**
   * Play Breath Awareness Tone - Optimized for Grounding and Centering
   * 
   * This specialized tone is designed to center attention and ground awareness
   * with each breath, using the natural frequency of 432 Hz with strong lower
   * frequency support to prevent high frequencies from dominating perception.
   * 
   * Design Philosophy:
   * - Balanced attack (1.2s): Natural centering, not too fast, not too slow
   * - High sustain (0.80): Steady, stable presence supporting breath rhythm
   * - Moderate release (0.6s): Clear end that respects the breathing cycle
   * - Grounding frequencies: 432 Hz primary + dual sub-bass support:
   *   - 216 Hz (1 octave below): Primary harmonic support
   *   - 108 Hz (2 octaves below): Deep grounding foundation
   * - Neutral filter: Maintains clarity without artificial warmth
   * - Minimal drift: Stable tone that doesn't distract from breath observation
   * 
   * Frequency Balance (Critical for breath work):
   * - Primary (432 Hz): 50% gain
   * - Mid-bass (216 Hz): 35% gain (strong presence, supports primary)
   * - Sub-bass (108 Hz): 20% gain (deep grounding, prevents dominance of highs)
   * 
   * Safety: All parameters are safe for human hearing (below 85dB at normal volumes)
   * 
   * @param {number} duration - Total duration in seconds (default: 10.0 for full meditation)
   */
  const playBreathAwarenessTone = useCallback((duration: number = 10.0): void => {
    const mainContext = audioManager.getMainAudioContext();
    const mainGain = audioManager.getMainMasterGain();

    if (!mainContext || !mainGain) {
      console.error('❌ Audio not initialized. Call initializeAudio() first.');
      return;
    }

    try {
      // Stop any existing single-tone oscillator
      if (oscillatorRef.current) {
        try {
          oscillatorRef.current.stop();
          oscillatorRef.current.disconnect();
        } catch (e) {
          // Already stopped
        }
      }

      const currentTime = mainContext.currentTime;

      // Stop any active oscillators from previous playback
      activeOscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {
          // Already stopped
        }
      });
      activeOscillatorsRef.current = [];

      // ===== PRIMARY TONE: 432 Hz (Natural frequency, A4 tuning) =====
      const primaryOsc = mainContext.createOscillator();
      primaryOsc.type = 'sine'; // Pure sine for clarity and centering
      primaryOsc.frequency.value = 432; // A4 in 432Hz tuning - "natural," "balanced"

      // ===== MID-BASS SUPPORT: 216 Hz (One octave below for harmonics) =====
      const midBassOsc = mainContext.createOscillator();
      midBassOsc.type = 'sine';
      midBassOsc.frequency.value = 216; // Harmonic support, prevents 432 from feeling thin

      // ===== SUB-BASS FOUNDATION: 108 Hz (Two octaves below for grounding) =====
      const subBassOsc = mainContext.createOscillator();
      subBassOsc.type = 'sine';
      subBassOsc.frequency.value = 108; // Deep grounding, keeps high frequencies from dominating

      // ===== NEUTRAL CLARITY FILTER =====
      // Doesn't artificially warm or brighten; maintains natural 432 Hz character
      const clarityFilter = mainContext.createBiquadFilter();
      clarityFilter.type = 'lowpass';
      clarityFilter.frequency.value = 1500; // Higher cutoff than golden light - preserves clarity
      clarityFilter.Q.value = 0.3; // Very gentle, almost transparent

      // ===== GAIN NODES FOR FREQUENCY BALANCE =====
      const primaryGain = mainContext.createGain();
      const midBassGain = mainContext.createGain();
      const subBassGain = mainContext.createGain();

      // ===== BREATH AWARENESS ADSR ENVELOPE =====
      // A = 1.2s (Natural centering pace)
      // D = 0.2s (Quick settle to sustain)
      // S = 0.80 (Steady, stable presence)
      // R = 0.6s (Respects breathing cycle - shorter than golden light)
      const breathAttack = 1.2;     // 1.2s - natural centering rhythm
      const breathDecay = 0.2;      // 0.2s - quick transition to sustain
      const breathSustain = 0.80;   // 80% sustain - steady, present
      const breathRelease = 0.6;    // 0.6s - respects natural breath cycle

      // ===== FREQUENCY BALANCE RATIO =====
      // Critical: Lower frequencies must not be overpowered by higher ones
      const primaryPeakGain = 0.50;       // Primary tone: 50% strength
      const midBassPeakGain = primaryPeakGain * 0.70;   // Mid-bass: 35% (70% of primary)
      const subBassPeakGain = primaryPeakGain * 0.40;   // Sub-bass: 20% (40% of primary)

      const primarySustainGain = primaryPeakGain * breathSustain;
      const midBassSustainGain = midBassPeakGain * breathSustain;
      const subBassSustainGain = subBassPeakGain * breathSustain;

      // ===== PRIMARY TONE ENVELOPE =====
      primaryGain.gain.setValueAtTime(0, currentTime);
      primaryGain.gain.linearRampToValueAtTime(primaryPeakGain, currentTime + breathAttack);
      primaryGain.gain.linearRampToValueAtTime(primarySustainGain, currentTime + breathAttack + breathDecay);
      primaryGain.gain.setValueAtTime(primarySustainGain, currentTime + duration - breathRelease);
      primaryGain.gain.linearRampToValueAtTime(0, currentTime + duration);

      // ===== MID-BASS TONE ENVELOPE (Slightly overlapped attack for smooth blend) =====
      midBassGain.gain.setValueAtTime(0, currentTime);
      midBassGain.gain.linearRampToValueAtTime(midBassPeakGain, currentTime + breathAttack * 0.9);
      midBassGain.gain.linearRampToValueAtTime(midBassSustainGain, currentTime + breathAttack + breathDecay);
      midBassGain.gain.setValueAtTime(midBassSustainGain, currentTime + duration - breathRelease);
      midBassGain.gain.linearRampToValueAtTime(0, currentTime + duration);

      // ===== SUB-BASS TONE ENVELOPE (Fastest attack, longest sustain for grounding) =====
      subBassGain.gain.setValueAtTime(0, currentTime);
      subBassGain.gain.linearRampToValueAtTime(subBassPeakGain, currentTime + breathAttack * 0.7);
      subBassGain.gain.linearRampToValueAtTime(subBassSustainGain, currentTime + breathAttack + breathDecay);
      subBassGain.gain.setValueAtTime(subBassSustainGain, currentTime + duration - breathRelease * 0.8);
      subBassGain.gain.linearRampToValueAtTime(0, currentTime + duration);

      // ===== MINIMAL ORGANIC VARIATION =====
      // Unlike golden light, breath awareness wants stability, so drift is very subtle
      const driftLFO = mainContext.createOscillator();
      const driftGain = mainContext.createGain();
      driftLFO.type = 'sine';
      driftLFO.frequency.value = 0.04; // Very slow drift (25 second period)
      driftGain.gain.value = 0.3; // Only 0.3 Hz variation - ~0.07% subtle centering shimmer

      driftLFO.connect(driftGain);
      driftGain.connect(primaryOsc.frequency);
      driftLFO.start(currentTime);
      driftLFO.stop(currentTime + duration);

      // ===== CONNECT AUDIO GRAPH =====
      // Primary: Oscillator -> Filter -> Gain -> Master
      primaryOsc.connect(clarityFilter);
      clarityFilter.connect(primaryGain);
      primaryGain.connect(mainGain);

      // Mid-bass: Direct to gain -> Master (no filter, full presence)
      midBassOsc.connect(midBassGain);
      midBassGain.connect(mainGain);

      // Sub-bass: Direct to gain -> Master (deepest grounding, unfiltered)
      subBassOsc.connect(subBassGain);
      subBassGain.connect(mainGain);

      // ===== START PLAYBACK =====
      primaryOsc.start(currentTime);
      primaryOsc.stop(currentTime + duration);

      midBassOsc.start(currentTime);
      midBassOsc.stop(currentTime + duration);

      subBassOsc.start(currentTime);
      subBassOsc.stop(currentTime + duration);

      // Store all oscillators for proper cleanup
      oscillatorRef.current = primaryOsc;
      activeOscillatorsRef.current = [primaryOsc, midBassOsc, subBassOsc, driftLFO];

      // Update state
      setState(prev => ({
        ...prev,
        isPlaying: true,
        currentFrequency: 432
      }));
    } catch (error) {
      console.error('❌ Error playing breath awareness tone:', error);
    }
  }, []);

  /**
   * Play Golden Light Tone - Optimized for Warmth and Healing
   * 
   * This specialized tone is designed to feel like plants soaking in the warmth
   * of the sun: a gradual, gentle onset with sustained warmth that lingers.
   * 
   * Design Philosophy:
   * - Extended attack (2s): Like sunrise gradually warming the Earth
   * - High sustain (0.85): Prolonged warmth that fills and holds
   * - Extended release (1s): Lingering warmth that fades gently
   * - Warm frequencies: 528 Hz primary + harmonic undertones at 264 Hz (octave below)
   * - Sweeping filter: Gently warms the tone over time
   * - Organic drift: Subtle frequency variation like natural sunlight fluctuation
   * 
   * Safety: All parameters are safe for human hearing (below 85dB at normal volumes)
   * 
   * @param {number} duration - Total duration in seconds (default: 8.0 for full meditation passage)
   */
  const playGoldenLightTone = useCallback((duration: number = 8.0): void => {
    const mainContext = audioManager.getMainAudioContext();
    const mainGain = audioManager.getMainMasterGain();

    if (!mainContext || !mainGain) {
      console.error('❌ Audio not initialized. Call initializeAudio() first.');
      return;
    }

    try {
      // Stop any existing single-tone oscillator
      if (oscillatorRef.current) {
        try {
          oscillatorRef.current.stop();
          oscillatorRef.current.disconnect();
        } catch (e) {
          // Already stopped
        }
      }

      const currentTime = mainContext.currentTime;

      // Stop any active oscillators from previous playback
      activeOscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {
          // Already stopped
        }
      });
      activeOscillatorsRef.current = [];

      // ===== PRIMARY TONE: 528 Hz (MI frequency - Love/Transformation) =====
      const primaryOsc = mainContext.createOscillator();
      primaryOsc.type = 'sine'; // Pure sine for calm, meditative quality
      primaryOsc.frequency.value = 528; // Solfeggio MI / Heart-centered meditation tone

      // ===== HARMONIC UNDERTONE: 264 Hz (One octave below for foundation) =====
      const undertoneOsc = mainContext.createOscillator();
      undertoneOsc.type = 'sine';
      undertoneOsc.frequency.value = 264; // Octave below for grounding warmth

      // ===== WARM LOW-PASS FILTER =====
      // Creates a "glowing" warmth by rolling off harsh high frequencies
      const warmthFilter = mainContext.createBiquadFilter();
      warmthFilter.type = 'lowpass';
      warmthFilter.frequency.value = 800; // Warm cutoff (allows fundamental but softens overtones)
      warmthFilter.Q.value = 0.4; // Gentle slope for smooth warmth

      // ===== DYNAMIC FILTER AUTOMATION =====
      // Gently opens filter over time for expanding, living quality
      warmthFilter.frequency.setValueAtTime(700, currentTime); // Start slightly warmer
      warmthFilter.frequency.linearRampToValueAtTime(900, currentTime + duration * 0.5); // Gradually expand mid-way
      warmthFilter.frequency.linearRampToValueAtTime(800, currentTime + duration); // Return to focal warmth

      // ===== PRIMARY TONE GAIN NODE (for ADSR envelope) =====
      const primaryGain = mainContext.createGain();
      const undertoneGain = mainContext.createGain();

      // ===== GOLDEN LIGHT ADSR ENVELOPE =====
      // A = 2.0s (slow sunrise rise)
      // D = 0.3s (natural settle)
      // S = 0.85 (high, nurturing sustain)
      // R = 1.0s (lingering warmth)
      const goldenAttack = 2.0;     // 2 seconds - like sun rising over horizon
      const goldenDecay = 0.3;      // 0.3 seconds - natural settling in
      const goldenSustain = 0.85;   // 85% - warm, full presence
      const goldenRelease = 1.0;    // 1.0 seconds - warmth slowly fades

      const primaryPeakGain = 0.4;     // Primary tone peak (meditative, not loud)
      const undertoneRatio = 0.25;      // Undertone at 25% of primary (foundational bass support)
      const primarySustainGain = primaryPeakGain * goldenSustain;
      const undertoneGain_value = primarySustainGain * undertoneRatio;

      // ===== PRIMARY TONE ENVELOPE =====
      primaryGain.gain.setValueAtTime(0, currentTime);
      
      // Attack: 0 -> peak over 2 seconds (gradual warmth)
      primaryGain.gain.linearRampToValueAtTime(
        primaryPeakGain,
        currentTime + goldenAttack
      );
      
      // Decay: peak -> sustain
      primaryGain.gain.linearRampToValueAtTime(
        primarySustainGain,
        currentTime + goldenAttack + goldenDecay
      );
      
      // Sustain: hold warm at sustain level
      primaryGain.gain.setValueAtTime(
        primarySustainGain,
        currentTime + duration - goldenRelease
      );
      
      // Release: sustain -> 0 (lingering warmth fade)
      primaryGain.gain.linearRampToValueAtTime(
        0,
        currentTime + duration
      );

      // ===== UNDERTONE ENVELOPE (slightly different shape for movement) =====
      undertoneGain.gain.setValueAtTime(0, currentTime);
      
      // Attack: slightly faster than primary (adds foundation)
      undertoneGain.gain.linearRampToValueAtTime(
        primaryPeakGain * undertoneRatio,
        currentTime + goldenAttack * 0.8
      );
      
      // Decay to sustain
      undertoneGain.gain.linearRampToValueAtTime(
        undertoneGain_value,
        currentTime + goldenAttack + goldenDecay
      );
      
      // Sustain
      undertoneGain.gain.setValueAtTime(
        undertoneGain_value,
        currentTime + duration - goldenRelease
      );
      
      // Release (slightly faster than primary for clarity)
      undertoneGain.gain.linearRampToValueAtTime(
        0,
        currentTime + duration - 0.2 // Fades slightly before primary ends
      );

      // ===== ORGANIC FREQUENCY DRIFT =====
      // Like sunlight fluctuating gently on leaves
      const driftLFO = mainContext.createOscillator();
      const driftGain = mainContext.createGain();
      driftLFO.type = 'sine';
      driftLFO.frequency.value = 0.07; // Very slow drift (14 second period)
      driftGain.gain.value = 0.8; // 0.8 Hz gentle variation on 528 = ±0.15% subtle shimmer

      driftLFO.connect(driftGain);
      driftGain.connect(primaryOsc.frequency);
      driftLFO.start(currentTime);
      driftLFO.stop(currentTime + duration);

      // ===== CONNECT AUDIO GRAPH =====
      // Primary: Oscillator -> Filter -> Gain -> Master
      primaryOsc.connect(warmthFilter);
      warmthFilter.connect(primaryGain);
      primaryGain.connect(mainGain);

      // Undertone: Oscillator -> Gain -> Master (bypasses filter for pure foundation bass)
      undertoneOsc.connect(undertoneGain);
      undertoneGain.connect(mainGain);

      // ===== START PLAYBACK =====
      primaryOsc.start(currentTime);
      primaryOsc.stop(currentTime + duration);

      undertoneOsc.start(currentTime);
      undertoneOsc.stop(currentTime + duration);

      // Store all oscillators for proper cleanup
      oscillatorRef.current = primaryOsc;
      activeOscillatorsRef.current = [primaryOsc, undertoneOsc, driftLFO];

      // Update state
      setState(prev => ({
        ...prev,
        isPlaying: true,
        currentFrequency: 528
      }));
    } catch (error) {
      console.error('❌ Error playing golden light tone:', error);
    }
  }, []);

  /**
   * Play a sacred chord (multiple frequencies simultaneously)
   * 
   * Chords create harmonic relationships that produce specific energetic effects.
   * Each chord combines multiple frequencies that resonate together,
   * creating a richer, more complex sonic experience than single tones.
   * 
   * Technical Implementation:
   * - Multiple oscillators play simultaneously
   * - Each frequency has independent gain control for balance
   * - Smooth fade-in and fade-out prevent clicks
   * - Fundamental frequency is louder, harmonics are softer (natural overtone series)
   * 
   * @param {string} chord - Chord type identifier (e.g., 'fr00t', 'harmony', 'love')
   */
  const playSacredChord = useCallback((chord: string): void => {
    // Get context and gain from AudioManager
    const mainContext = audioManager.getMainAudioContext();
    const mainGain = audioManager.getMainMasterGain();

    if (!mainContext || !mainGain) {
      console.error('❌ Audio not initialized. Call initializeAudio() first.');
      return;
    }

    try {
      // Stop any active oscillators from previous chords
      activeOscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {
          // Already stopped
        }
      });
      activeOscillatorsRef.current = [];

      /**
       * Sacred Chord Definitions
       * Each chord is a combination of frequencies that create specific energetic effects
       * Frequencies are carefully chosen based on harmonic relationships and spiritual significance
       */
      const chordDefinitions: { [key: string]: number[] } = {
        // Fr00t chord - All 4 Fr00t frequencies in perfect harmony
        // F(432) + R(528) + 0(396) + T(639) = Complete Fr00t resonance
        'fr00t': [432, 528, 396, 639],
        
        // Harmony chord - Perfect fifth relationships for balance
        // Creates a balanced, centered resonance
        'harmony': [396, 528, 639],
        
        // Solfeggio chord - Historical medieval frequencies
        // Liberation + Transformation + Spiritual Order
        'healing': [396, 528, 852],
        
        // Chakra chord - All 7 chakra centers
        // Complete energy system activation from root to crown
        'chakra': [396, 417, 528, 639, 741, 852, 963],
        
        // Love chord - Frequencies of compassion, connection, and expression
        // Frequency combination: 528 + 639 + 741 Hz
        'love': [528, 639, 741],
        
        // Void chord - Frequencies of infinite potential and transformation
        // Liberation + Change + Spiritual Order + Crown activation
        'void': [396, 417, 852, 963],
        
        // Manifestation chord - Power and creation frequencies
        // Transformation + Connection + Intuition for manifesting reality
        'manifest': [528, 639, 852],
        
        // Protection chord - Grounding and shielding frequencies
        // Root chakra + Sacral + Heart for complete protection
        'protect': [396, 417, 639],
        
        // Power chord - Authority, strength, and solar plexus activation
        // Solar plexus (528) + Throat (741) + Crown (963) for personal power
        'power': [528, 741, 963],
        
        // Wisdom chord - Knowledge, expression, and divine connection
        // Love (528) + Expression (741) + Divine connection (963)
        'wisdom': [528, 741, 963]
      };

      const frequencies = chordDefinitions[chord] || [432];
      const currentTime = mainContext.currentTime;
      const duration = 3.0; // 3 second chord

      // Create oscillators for each frequency in the chord
      frequencies.forEach((freq, index) => {
        const osc = mainContext.createOscillator();
        const oscGain = mainContext.createGain();
        
        // Use sine wave for pure tones
        osc.type = 'sine';
        osc.frequency.value = freq;
        
        // Vary volume by position for depth (fundamental louder, harmonics softer)
        const volumeMultiplier = index === 0 ? 0.4 : 0.25 / (index + 1);
        oscGain.gain.value = volumeMultiplier;
        
        // Smooth fade in and out
        oscGain.gain.setValueAtTime(0, currentTime);
        oscGain.gain.linearRampToValueAtTime(volumeMultiplier, currentTime + 0.1);
        oscGain.gain.setValueAtTime(volumeMultiplier, currentTime + duration - 0.3);
        oscGain.gain.linearRampToValueAtTime(0, currentTime + duration);
        
        // Connect: oscillator -> gain -> main gain -> reverb
        osc.connect(oscGain);
        oscGain.connect(mainGain);
        
        // Start and stop
        osc.start(currentTime);
        osc.stop(currentTime + duration);
        
        activeOscillatorsRef.current.push(osc);
      });

      setState(prev => ({
        ...prev,
        isPlaying: true,
        currentFrequency: frequencies[0]
      }));

      // Reset playing state after duration
      setTimeout(() => {
        setState(prev => ({
          ...prev,
          isPlaying: false
        }));
        activeOscillatorsRef.current = [];
      }, duration * 1000);

      console.log(`🎶 Sacred Chord: ${chord} (${frequencies.length} frequencies)`);
      console.log(`   Frequencies: ${frequencies.map(f => f.toFixed(0) + 'Hz').join(', ')}`);
      
    } catch (error) {
      console.error('❌ Failed to play sacred chord:', error);
    }
  }, []);

  // ============================================================================
  // SACRED SCALE PLAYBACK - Sequential Melodic System
  // ============================================================================

  /**
   * Play a sacred scale (sequence of frequencies played one after another)
   * 
   * Scales create melodic progressions that can induce specific states of consciousness.
   * Unlike chords where all notes play together, scales play sequentially,
   * creating a journey through frequency space.
   * 
   * Technical Implementation:
   * - Each note plays sequentially with defined duration
   * - Professional ADSR envelope on each note
   * - Timing managed via setTimeout for precise scheduling
   * - Each note has natural attack, decay, sustain, and release
   * 
   * Musical Applications:
   * - Meditation sequences
   * - Chakra activation progressions
   * - Healing frequency journeys
   * - Consciousness elevation practices
   * 
   * @param {string} scale - Scale type identifier (e.g., 'solfeggio', 'chakra', 'fr00t')
   */
  const playSacredScale = useCallback((scale: string): void => {
    // Get context and gain from AudioManager
    const mainContext = audioManager.getMainAudioContext();
    const mainGain = audioManager.getMainMasterGain();

    if (!mainContext || !mainGain) {
      console.error('❌ Audio not initialized. Call initializeAudio() first.');
      return;
    }

    try{
      /**
       * Sacred Scale Definitions
       * Each scale is a sequence of frequencies played one after another
       * Scales create melodic progressions that can induce specific states of consciousness
       */
      const scaleDefinitions: { [key: string]: number[] } = {
        // Solfeggio scale - Ancient six-tone scale from medieval chants
        // Each frequency corresponds to a specific spiritual effect
        'solfeggio': [396, 417, 528, 639, 741, 852],
        
        // Chakra scale - Ascending through all seven energy centers
        // Starts at root chakra and rises to crown
        'chakra': [396, 417, 528, 639, 741, 852, 963],
        
        // Fr00t scale - Journey through Fr00t numerology frequencies
        // 0(396) -> F(432) -> R(528) -> T(639)
        'fr00t': [396, 432, 528, 639],
        
        // Healing scale - Frequencies optimized for transformation and healing
        // Combines liberation, change, transformation, awakening, intuition, and crown
        'healing': [396, 417, 528, 741, 852, 963],
        
        // Ascending scale - Pythagorean tuning from 432Hz base
        // Each step is proportional using ancient Greek ratios
        'ascending': [432, 486, 540, 594, 648, 702, 756, 810],
        
        // Golden ratio scale - Based on φ (phi = 1.618...) exponential progression
        // Each note is the previous multiplied by the golden ratio
        'golden': [
          432,                              // Base (A4 natural tuning)
          432 * 1.618,                      // 698.976 Hz
          432 * Math.pow(1.618, 2),        // 1130.918 Hz
          432 * Math.pow(1.618, 3)         // 1829.805 Hz
        ],
        
        // Harmonic series - Natural overtones of 432Hz fundamental
        // Found naturally in resonating strings and air columns
        'harmonic': [
          432,      // Fundamental (1st harmonic)
          864,      // 2nd harmonic (octave)
          1296,     // 3rd harmonic (perfect fifth + octave)
          1728,     // 4th harmonic (two octaves)
          2160      // 5th harmonic (major third + two octaves)
        ],
        
        // Pentatonic scale - Five-tone scale found across cultures
        // Uses chakra frequencies for spiritual resonance
        'pentatonic': [396, 528, 639, 741, 963]
      };

      const frequencies = scaleDefinitions[scale] || [432, 528, 639];
      const noteDuration = 600; // milliseconds per note

      // Play each note in sequence
      frequencies.forEach((freq, index) => {
        setTimeout(() => {
          // Re-check context availability in timeout
          const ctx = audioManager.getMainAudioContext();
          const gain = audioManager.getMainMasterGain();
          
          if (ctx && gain) {
            const osc = ctx.createOscillator();
            const oscGain = ctx.createGain();
            
            osc.type = 'sine';
            osc.frequency.value = freq;
            
            // Note envelope (attack-decay-sustain-release)
            const now = ctx.currentTime;
            const attackTime = 0.05;
            const decayTime = 0.1;
            const sustainLevel = 0.3;
            const releaseTime = 0.2;
            const noteDur = 0.4;
            
            oscGain.gain.setValueAtTime(0, now);
            oscGain.gain.linearRampToValueAtTime(0.5, now + attackTime);
            oscGain.gain.linearRampToValueAtTime(sustainLevel, now + attackTime + decayTime);
            oscGain.gain.setValueAtTime(sustainLevel, now + noteDur - releaseTime);
            oscGain.gain.linearRampToValueAtTime(0, now + noteDur);
            
            osc.connect(oscGain);
            oscGain.connect(gain);
            
            osc.start(now);
            osc.stop(now + noteDur);
            
            if (index === 0) {
              setState(prev => ({
                ...prev,
                isPlaying: true,
                currentFrequency: freq
              }));
            }
          }
        }, index * noteDuration);
      });

      // Reset state after complete scale plays
      const totalDuration = frequencies.length * noteDuration + 500;
      setTimeout(() => {
        setState(prev => ({
          ...prev,
          isPlaying: false
        }));
      }, totalDuration);

      console.log(`🎶 Sacred Scale: ${scale} (${frequencies.length} notes)`);
      console.log(`   Frequencies: ${frequencies.map(f => f.toFixed(0) + 'Hz').join(' → ')}`);
      console.log(`   Total duration: ${(totalDuration / 1000).toFixed(1)}s`);
      
    } catch (error) {
      console.error('❌ Failed to play sacred scale:', error);
    }
  }, []);

  // ============================================================================
  // AUDIO CONTROL FUNCTIONS
  // ============================================================================

  /**
   * Stop all audio playback immediately
   * Stops both single tones and polyphonic chords/scales
   */
  const stopAudio = useCallback((): void => {
    // Stop single-tone oscillator
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
        oscillatorRef.current = null;
      } catch (error) {
        // Oscillator might already be stopped
      }
    }

    // Stop all active oscillators (chords, specialized tones with multiple oscillators)
    activeOscillatorsRef.current.forEach(osc => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        // Already stopped
      }
    });
    activeOscillatorsRef.current = [];

    setState(prev => ({
      ...prev,
      isPlaying: false
    }));
  }, []);

  /**
   * Set master volume level
   * 
   * Volume is specified in decibels (dB) and converted to linear gain.
   * 
   * Decibel Formula: gain = 10^(dB/20)
   * 
   * Volume Scale:
   * - 0 dB = Full volume (gain = 1.0)
   * - -20 dB = ~10% volume (gain = 0.1) - Default
   * - -40 dB = ~1% volume (gain = 0.01) - Very quiet
   * 
   * Why use dB instead of linear gain?
   * - Human hearing is logarithmic, not linear
   * - dB scale matches how we perceive loudness
   * - Industry standard for audio engineering
   * 
   * @param {number} volume - Volume in decibels (-40 to 0)
   */
  const setVolume = useCallback((volume: number): void => {
    // Validate volume range
    if (volume < VOLUME_RANGE.min || volume > VOLUME_RANGE.max) {
      console.warn(`⚠️ Volume ${volume}dB out of range. Clamping to ${VOLUME_RANGE.min}-${VOLUME_RANGE.max}dB`);
      volume = Math.max(VOLUME_RANGE.min, Math.min(VOLUME_RANGE.max, volume));
    }

    // Use AudioManager to set main volume
    audioManager.setMainVolume(volume);

    // Update state
    setState(prev => ({
      ...prev,
      volume
    }));
  }, []);

  // ============================================================================
  // NUMEROLOGY FREQUENCY GENERATOR
  // ============================================================================

  /**
   * Generate a sacred frequency from a numerology number
   * 
   * Maps numerological values (1-9, 11, 22, 33) to frequencies.
   * Uses numerological reduction (adding digits until single digit or master number).
   * 
   * Base: 432 Hz (natural tuning, A4)
   * Multiplier: 1 + (reduced_number / 9)
   * 
   * This creates a range from 432 Hz (number 0) to ~864 Hz (number 9).
   * Master numbers (11, 22, 33) retain their special significance.
   * 
   * @param {number} number - Any positive integer
   * @returns {number} Corresponding frequency in Hz
   */
  const generateSacredFrequency = useCallback((number: number): number => {
    // Base frequency (A4 = 432Hz, sacred tuning)
    const baseFrequency = 432;
    
    // Calculate numerological reduction
    let reduced = number;
    while (reduced > 9 && reduced !== 11 && reduced !== 22 && reduced !== 33) {
      reduced = String(reduced).split('').reduce((sum, digit) => sum + parseInt(digit), 0);
    }

    // Map reduced number to frequency
    const multiplier = 1 + (reduced / 9);
    return baseFrequency * multiplier;
  }, []);

  // Listen for AudioManager initialization
  useEffect(() => {
    // If AudioManager is already ready, initialize immediately
    if (audioManager.isReady() && !state.isInitialized) {
      initializeAudio();
    }

    // Subscribe to AudioManager initialization
    const unsubscribe = audioManager.onInitialized(() => {
      console.log('🎵 Sacred Audio: AudioManager initialized, setting up...');
      initializeAudio();
    });

    return unsubscribe;
  }, [initializeAudio, state.isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudio();
      // AudioManager handles context cleanup - no need to close here
    };
  }, [stopAudio]);

  // ============================================================================
  // RETURN COMPLETE AUDIO INTERFACE
  // ============================================================================

  return {
    // Audio State
    ...state,
    sacredTones: FREQUENCY_SETS.fr00t,
    
    // Audio Controls
    initializeAudio,
    initializeSacredAudio,
    playSacredTone,
    playBreathAwarenessTone,
    playGoldenLightTone,
    playSacredChord,
    playSacredScale,
    stopAudio,
    setVolume,
    generateSacredFrequency
  };
};

// Default export for convenience
export default useSacredAudio;
